import React from 'react';
import { Users, Heart, Target, TrendingUp, Leaf, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1591197172062-c718f82aba20)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/85 to-emerald-800/70"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            About Delinam Foundation
          </h1>
          <p className="text-xl text-emerald-50 max-w-2xl mx-auto">
            Building stronger communities through collaboration, sustainability, and empowerment
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-8">
                <img 
                  src="/delinam-logo.png"
                  alt="Delinam Foundation Logo"
                  className="h-20 w-auto object-contain mb-6"
                />
                <h2 className="text-3xl font-bold text-gray-900">About Delinam Foundation</h2>
              </div>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Delinam Foundation is a community-rooted nonprofit organization committed to empowering 
                  individuals and strengthening communities through sustainable development, youth empowerment, 
                  and leadership development.
                </p>
                <p>
                  Founded on the belief that meaningful change begins at the grassroots level, we work 
                  alongside communities to design practical, culturally grounded solutions that address 
                  social and economic challenges. Our programs focus on building skills, improving access 
                  to resources, and supporting local leadership to create long-term, self-sustaining impact.
                </p>
                <p>
                  We believe in dignity, collaboration, and accountability. Every initiative is shaped by 
                  community voices and measured by real outcomes — improved livelihoods, empowered youth, 
                  and resilient local leadership.
                </p>
              </div>
            </div>
            <div className="relative h-[400px] rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1760549255949-767d18981890"
                alt="Community collaboration"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-b from-emerald-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-emerald-200 shadow-lg">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-emerald-600 rounded-lg flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed">
                  To empower communities by supporting education, youth development, sustainable 
                  livelihoods, and leadership initiatives that create lasting social and economic impact.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-200 shadow-lg">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-emerald-600 rounded-lg flex items-center justify-center mb-6">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed">
                  Thriving, self-reliant communities where individuals—especially young people—have the 
                  tools, confidence, and opportunities to shape their futures.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <Card className="border-2 border-emerald-100 hover:border-emerald-300 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Community First</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We work with communities, not for them
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-100 hover:border-emerald-300 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Heart className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Dignity & Respect</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Every person deserves opportunity and voice
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-100 hover:border-emerald-300 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Sustainability</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We prioritize long-term impact over short-term fixes
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-100 hover:border-emerald-300 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Accountability</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We steward resources responsibly
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-emerald-100 hover:border-emerald-300 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Collaboration</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      We grow through partnerships and shared purpose
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Founder's Note */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Founder's Note</h2>
            <Card className="border-2 border-emerald-200 shadow-lg">
              <CardContent className="p-10">
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p className="text-lg">
                    Delinam Foundation was created from a simple but powerful belief: real change begins 
                    when people are given the opportunity to shape their own futures.
                  </p>
                  <p>
                    Growing up, I saw how communities thrive when individuals are supported with the right 
                    tools, encouragement, and access to opportunity. I also saw how potential is often 
                    limited—not by lack of talent or ambition—but by lack of access to resources and 
                    support systems.
                  </p>
                  <p>
                    Delinam Foundation exists to help close that gap. Our mission is grounded in partnership, 
                    dignity, and long-term thinking. We work alongside communities to support sustainable 
                    development, youth empowerment, and leadership—because lasting change is built from within.
                  </p>
                  <p>
                    Thank you for believing in this mission and for being part of the journey.
                  </p>
                  <p className="italic text-emerald-800 font-medium pt-4">
                    — Founder, Delinam Foundation
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;
